/**
 * A table's columns, widened and narrowed by dragging the edge between two
 * headings, and kept that way (a view preference, in the browser). Widths
 * are fractions of the table, so they fit however wide its pane is. The edge
 * takes the arrow keys too, and a double-click puts the defaults back.
 */
import { useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { loadColumnWidths, saveColumnWidths } from "./preferences";

/** No column narrower than this share of the table: always something to grab. */
const MINIMUM = 0.12;
const STEP = 0.02;

export interface Column {
  heading: ReactNode;
  /** Its share of the table by default; the defaults sum to 1. */
  width: number;
  className?: string;
}

/** The widths for `table`: as this person left them, or the defaults. */
function initial(table: string, columns: readonly Column[]): number[] {
  return loadColumnWidths(table, columns.length) ?? columns.map((column) => column.width);
}

/** Move the edge after column `at` by `delta` of the table, keeping both sides above the minimum. */
export function moveEdge(widths: readonly number[], at: number, delta: number): number[] {
  const left = widths[at]!;
  const right = widths[at + 1]!;
  const moved = Math.max(MINIMUM - left, Math.min(right - MINIMUM, delta));
  return widths.map((width, index) => (index === at ? left + moved : index === at + 1 ? right - moved : width));
}

/**
 * The column group and heading row of a table whose columns resize. `table`
 * names it among the tables whose widths are kept.
 */
export function ResizableHead({ table, columns }: { table: string; columns: readonly Column[] }) {
  const [widths, setWidths] = useState(() => initial(table, columns));
  const row = useRef<HTMLTableRowElement>(null);
  const drag = useRef<{ at: number; x: number; span: number; from: number[] } | null>(null);
  const keep = (next: number[] | null) => {
    setWidths(next ?? columns.map((column) => column.width));
    saveColumnWidths(table, next);
  };
  const start = (at: number) => (event: PointerEvent<HTMLSpanElement>) => {
    const span = row.current?.getBoundingClientRect().width ?? 0;
    if (span <= 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = { at, x: event.clientX, span, from: widths };
  };
  const move = (event: PointerEvent<HTMLSpanElement>) => {
    const now = drag.current;
    if (!now) return;
    setWidths(moveEdge(now.from, now.at, (event.clientX - now.x) / now.span));
  };
  const end = () => {
    if (!drag.current) return;
    drag.current = null;
    saveColumnWidths(table, widths);
  };
  const key = (at: number) => (event: KeyboardEvent<HTMLSpanElement>) => {
    const delta = event.key === "ArrowLeft" ? -STEP : event.key === "ArrowRight" ? STEP : 0;
    if (!delta) return;
    event.preventDefault();
    keep(moveEdge(widths, at, delta));
  };
  const percent = (width: number) => `${(width * 100).toFixed(2)}%`;
  return (
    <>
      <colgroup>
        {widths.map((width, at) => (
          <col key={at} style={{ width: percent(width) }} />
        ))}
      </colgroup>
      <thead>
        <tr ref={row}>
          {columns.map((column, at) => (
            <th key={at} scope="col" className={column.className}>
              {column.heading}
              {at < columns.length - 1 && (
                <span
                  className="ws-col-resize"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`Resize ${typeof column.heading === "string" ? column.heading : `column ${at + 1}`}`}
                  aria-valuenow={Math.round(widths[at]! * 100)}
                  aria-valuemin={Math.round(MINIMUM * 100)}
                  aria-valuemax={Math.round((widths[at]! + widths[at + 1]! - MINIMUM) * 100)}
                  tabIndex={0}
                  title="Drag to resize; double-click for the default widths"
                  onPointerDown={start(at)}
                  onPointerMove={move}
                  onPointerUp={end}
                  onPointerCancel={end}
                  onKeyDown={key(at)}
                  onDoubleClick={() => keep(null)}
                />
              )}
            </th>
          ))}
        </tr>
      </thead>
    </>
  );
}
